import type { PlayerId, PlayerZones } from "@cloudnest/warchest-engine";
import { WC_FACTION_NAME, WC_UNIT_NAME } from "../i18n";
import {
  PLAYER_COLORS,
  PLAYER_DARK_COLORS,
  WarchestUnitSymbolIcon,
} from "./WarchestUnitSymbol";

const UNKNOWN = "UNKNOWN";

interface ZonePanelProps {
  readonly player: PlayerId;
  readonly zones: PlayerZones;
  readonly isActive: boolean;
  readonly isInitiativeHolder: boolean;
  readonly isMe: boolean;
  readonly selectedCoin: string | null;
  readonly onCoinSelect: (coinType: string | null) => void;
  // Unit types fully wiped out of play for this player (design-ref Supply
  // elimination overlay). Computed by the parent via getEliminatedUnits.
  readonly eliminatedUnits?: readonly string[];
}

const PLAYER_LABEL = WC_FACTION_NAME;
const FACTION_TOKEN: Record<PlayerId, string> = {
  P1: "var(--cn-warchest-raven)",
  P2: "var(--cn-warchest-wolf)",
};

// Prevent 4-char slice collisions (e.g. ROYAL vs ROYAL_GUARD both → "ROYA")
const COIN_LABELS: Record<string, string> = {
  ROYAL_GUARD: "R.GD",
  LIGHT_CAVALRY: "L.CV",
  WARRIOR_PRIEST: "W.PR",
};
function coinLabel(coinType: string): string {
  return COIN_LABELS[coinType] ?? coinType.slice(0, 4);
}

function groupByType(coins: readonly string[]): Record<string, number> {
  const result: Record<string, number> = {};
  for (const c of coins) {
    result[c] = (result[c] ?? 0) + 1;
  }
  return result;
}

function InitiativeBadge() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" role="img">
      <title>선공권 보유</title>
      <path
        d="M3,16 L3,9 L7,13 L10,5 L13,13 L17,9 L17,16 Z"
        fill="var(--cn-warchest-selected)"
        opacity={0.9}
      />
      <line
        x1="3"
        y1="16"
        x2="17"
        y2="16"
        stroke="var(--cn-warchest-selected)"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </svg>
  );
}

function CoinCard({
  coinType,
  owner,
  selected,
  onClick,
  testId,
}: {
  coinType: string;
  owner: PlayerId;
  selected: boolean;
  onClick?: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-player={owner}
      data-coin-type={coinType}
      onClick={onClick}
      title={WC_UNIT_NAME[coinType] ?? coinType}
      className="flex flex-col items-center justify-center transition-all"
      style={{
        width: "var(--cn-warchest-coin-size)",
        height: "var(--cn-warchest-coin-size)",
        borderRadius: "50%",
        gap: "2px",
        padding: 0,
        border: `3px solid ${selected ? "var(--cn-warchest-selected)" : "var(--cn-warchest-coin-rim-stroke)"}`,
        backgroundColor: selected
          ? "var(--cn-warchest-coin-rim)"
          : PLAYER_COLORS[owner],
        boxShadow: selected
          ? "0 0 0 2px var(--cn-warchest-selected), 0 2px 8px rgba(0,0,0,0.25)"
          : "0 2px 6px rgba(0,0,0,0.2)",
      }}
    >
      <WarchestUnitSymbolIcon
        unitType={coinType}
        owner={owner}
        size={24}
        symbolColor={selected ? PLAYER_COLORS[owner] : "rgba(255,255,255,0.92)"}
      />
      <span
        className="font-mono leading-none"
        style={{
          fontSize: "8px",
          color: selected
            ? "var(--cn-warchest-text)"
            : "rgba(255,255,255,0.85)",
        }}
      >
        {coinLabel(coinType)}
      </span>
    </button>
  );
}

function UnknownCoin() {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        width: "var(--cn-warchest-coin-size)",
        height: "var(--cn-warchest-coin-size)",
        borderRadius: "50%",
        border: "3px solid var(--cn-warchest-coin-rim-stroke)",
        backgroundColor: "var(--cn-warchest-hex-edge)",
      }}
    >
      <span
        className="font-bold text-xl"
        style={{ color: "var(--cn-warchest-card-bg)", opacity: 0.7 }}
      >
        ?
      </span>
    </div>
  );
}

function DiscardCoin({
  coinType,
  owner,
}: {
  coinType: string;
  owner: PlayerId;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center"
      title={WC_UNIT_NAME[coinType] ?? coinType}
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        gap: "2px",
        border: `2px solid ${PLAYER_DARK_COLORS[owner]}`,
        backgroundColor: PLAYER_COLORS[owner],
        opacity: 0.55,
        padding: 0,
      }}
    >
      <WarchestUnitSymbolIcon
        unitType={coinType}
        owner={owner}
        size={18}
        symbolColor="rgba(255,255,255,0.92)"
      />
      <span
        className="font-mono leading-none"
        style={{ fontSize: "7px", color: "rgba(255,255,255,0.85)" }}
      >
        {coinLabel(coinType)}
      </span>
    </div>
  );
}

export function WarchestZonePanel({
  player,
  zones,
  isActive,
  isInitiativeHolder,
  isMe,
  selectedCoin,
  onCoinSelect,
  eliminatedUnits = [],
}: ZonePanelProps) {
  const isOpponent = !isMe;

  const discardGroups = groupByType(zones.discardFaceUp);
  const supplyGroups = groupByType(zones.supply);

  // Stable hand keys: track per-type occurrence index
  const handTypeCounts: Record<string, number> = {};

  const factionColor = FACTION_TOKEN[player];

  return (
    <div
      className="space-y-2 rounded-xl border-2 p-3"
      style={{
        background: "var(--cn-warchest-card-bg)",
        borderColor: factionColor,
        boxShadow: isActive ? `0 0 0 2px ${factionColor}` : "none",
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="font-bold text-base" style={{ color: factionColor }}>
          {PLAYER_LABEL[player]}
        </span>
        {isMe && (
          <span
            className="font-medium text-xs"
            style={{ color: "var(--cn-warchest-text-muted)" }}
          >
            (나)
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {isInitiativeHolder && <InitiativeBadge />}
          {isActive && (
            <span
              className="rounded-full px-2 py-0.5 font-semibold text-xs"
              style={{
                background: factionColor,
                color: "var(--cn-warchest-card-bg)",
              }}
            >
              ▶
            </span>
          )}
        </div>
      </div>

      {/* Hand */}
      <div>
        <div className="mb-1.5 font-medium text-gray-500 text-xs">
          손패 ({zones.hand.length})
        </div>
        <div
          data-testid={isMe ? "warchest-hand" : `warchest-hand-${player}`}
          className="flex min-h-[52px] flex-wrap gap-1 sm:gap-1.5"
        >
          {zones.hand.map((coinType: string) => {
            handTypeCounts[coinType] = (handTypeCounts[coinType] ?? 0) + 1;
            const occurrence = handTypeCounts[coinType] ?? 1;
            const stableKey = `hand-${coinType}-${occurrence}`;
            return isOpponent || coinType === UNKNOWN ? (
              <UnknownCoin key={stableKey} />
            ) : (
              <CoinCard
                key={stableKey}
                testId={`warchest-hand-coin-${coinType}-${occurrence}`}
                coinType={coinType}
                owner={player}
                selected={selectedCoin === coinType}
                onClick={() =>
                  onCoinSelect(selectedCoin === coinType ? null : coinType)
                }
              />
            );
          })}
        </div>
      </div>

      {/* Bag / Discard counts */}
      <div className="flex items-center gap-3 text-gray-500 text-xs">
        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-600">
          🎒 {zones.bag.length}
        </span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-600">
          ↓ {zones.discardFaceDown.length}
        </span>
      </div>

      {/* Discard face-up — own player */}
      {!isOpponent && zones.discardFaceUp.length > 0 && (
        <div>
          <div className="mb-1 font-medium text-gray-500 text-xs">
            사용한 더미 ({zones.discardFaceUp.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {(Object.entries(discardGroups) as [string, number][]).map(
              ([coinType, count]) => (
                <div key={coinType} className="relative">
                  <DiscardCoin coinType={coinType} owner={player} />
                  {count > 1 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-gray-500 font-bold text-[9px] text-white">
                      {count}
                    </span>
                  )}
                </div>
              ),
            )}
          </div>
        </div>
      )}

      {/* Discard face-up — opponent (show count only) */}
      {isOpponent && zones.discardFaceUp.length > 0 && (
        <div className="text-gray-500 text-xs">
          ↑ 사용한 더미: {zones.discardFaceUp.length}
        </div>
      )}

      {/* Supply — own player only */}
      {!isOpponent && zones.supply.length > 0 && (
        <div>
          <div className="mb-1 font-medium text-gray-500 text-xs">
            보급 ({zones.supply.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {(Object.entries(supplyGroups) as [string, number][]).map(
              ([coinType, count]) => (
                <div
                  key={coinType}
                  className="flex items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5"
                >
                  <WarchestUnitSymbolIcon
                    unitType={coinType}
                    owner={player}
                    size={14}
                  />
                  <span className="font-mono text-[9px] text-gray-600">
                    {coinLabel(coinType)} ×{count}
                  </span>
                </div>
              ),
            )}
          </div>
        </div>
      )}

      {/* Eliminated units — own player only (design-ref Supply elimination) */}
      {!isOpponent && eliminatedUnits.length > 0 && (
        <div data-testid="warchest-supply-eliminated">
          <div className="mb-1 font-medium text-gray-500 text-xs">
            제거됨 ({eliminatedUnits.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {eliminatedUnits.map((coinType) => (
              <div
                key={coinType}
                data-testid={`warchest-supply-eliminated-${coinType}`}
                title={`${WC_UNIT_NAME[coinType] ?? coinType} 제거됨`}
                className="flex items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 opacity-50"
              >
                <span className="relative">
                  <WarchestUnitSymbolIcon
                    unitType={coinType}
                    owner={player}
                    size={14}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-[11px] text-red-600">
                    ✕
                  </span>
                </span>
                <span className="font-mono text-[9px] text-gray-500 line-through">
                  {WC_UNIT_NAME[coinType] ?? coinType}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
